import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    arrayStrings,
    formatCountMetric,
    formatStatusLabel,
    isRecord,
    JsonValue,
    numberValue,
    renderUntrustedString,
    stringValue,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

type OutreachReconciliationCaregiver = {
    readonly caregiverId: string;
    readonly caregiverName: string | null;
    readonly savedSuggestion: boolean;
    readonly suggestionSourceKind: string | null;
    readonly suggestionSourceMode: string | null;
    readonly rawCoveredShiftIds: ReadonlyArray<string>;
    readonly coveredShiftGroupIds: ReadonlyArray<string>;
    readonly expandedCoveredShiftIds: ReadonlyArray<string>;
    readonly groupExpansionStatus: string | null;
    readonly selectedOrSubmitted: boolean;
    readonly submittedShiftIds: ReadonlyArray<string>;
    readonly contactAttemptCount: number | null;
    readonly shiftResponseCount: number | null;
    readonly droppedOrSkippedReason: string | null;
    readonly evidenceStatus: string | null;
};

type OutreachReconciliationPayload = {
    readonly summary: Record<string, unknown>;
    readonly mismatches: Record<string, unknown>;
    readonly caregivers: ReadonlyArray<OutreachReconciliationCaregiver>;
};

export function OutreachReconciliationArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const reconciliation = getOutreachReconciliationPayload(artifact.payload);
    const metrics = [
        ['Caregivers', numberValue(reconciliation.summary['caregiver_count'])],
        [
            'Saved suggestions',
            numberValue(reconciliation.summary['saved_suggestion_count']),
        ],
        [
            'Submitted',
            numberValue(reconciliation.summary['submitted_caregiver_count']),
        ],
        [
            'Suggested not submitted',
            arrayStrings(
                reconciliation.mismatches['suggested_but_not_submitted'],
            ).length,
        ],
        [
            'Submitted without attempt',
            arrayStrings(
                reconciliation.mismatches['submitted_but_no_contact_attempt'],
            ).length,
        ],
        [
            'Attempts without responses',
            arrayStrings(
                reconciliation.mismatches[
                    'contact_attempt_exists_but_no_shift_responses'
                ],
            ).length,
        ],
    ] as const;
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="border-border/40 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t pt-2.5 sm:grid-cols-3">
                {metrics.map(([label, value]) => (
                    <div key={label}>
                        <div className="text-muted-foreground/70 text-[11px]">
                            {label}
                        </div>
                        <div className="text-foreground mt-0.5 text-[13px] font-medium tabular-nums">
                            {formatCountMetric(value)}
                        </div>
                    </div>
                ))}
            </div>
            {reconciliation.caregivers.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-xs">
                        <thead>
                            <tr className="border-border/60 text-muted-foreground border-b text-[11px]">
                                <th className="py-1.5 pr-3 font-medium">
                                    Caregiver
                                </th>
                                <th className="py-1.5 pr-3 font-medium">
                                    Suggestion
                                </th>
                                <th className="py-1.5 pr-3 font-medium">
                                    Coverage
                                </th>
                                <th className="py-1.5 pr-3 font-medium">
                                    Start payload
                                </th>
                                <th className="py-1.5 pr-3 font-medium">
                                    Attempts
                                </th>
                                <th className="py-1.5 font-medium">Evidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reconciliation.caregivers.map((caregiver) => (
                                <OutreachReconciliationCaregiverRow
                                    key={caregiver.caregiverId}
                                    caregiver={caregiver}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-muted-foreground text-xs">
                    No caregiver reconciliation rows were included.
                </p>
            )}
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}

function OutreachReconciliationCaregiverRow({
    caregiver,
}: {
    readonly caregiver: OutreachReconciliationCaregiver;
}) {
    return (
        <tr className="border-border/40 border-t align-top first:border-t-0">
            <td className="space-y-0.5 py-2 pr-3">
                <div className="text-foreground font-medium">
                    {caregiver.caregiverName
                        ? renderUntrustedString(caregiver.caregiverName)
                        : 'Unknown caregiver'}
                </div>
                <div className="text-muted-foreground/70 font-mono text-[11px] break-all">
                    {caregiver.caregiverId}
                </div>
            </td>
            <td className="space-y-0.5 py-2 pr-3">
                <div className="text-foreground">
                    {caregiver.savedSuggestion ? 'Saved' : 'Not saved'}
                </div>
                <div className="text-muted-foreground/70 text-[11px]">
                    {formatSuggestionSource(caregiver)}
                </div>
            </td>
            <td className="space-y-1 py-2 pr-3">
                <CoverageLine
                    label="Raw"
                    values={caregiver.rawCoveredShiftIds}
                />
                <CoverageLine
                    label="Groups"
                    values={caregiver.coveredShiftGroupIds}
                />
                <CoverageLine
                    label="Expanded"
                    values={caregiver.expandedCoveredShiftIds}
                />
                {caregiver.groupExpansionStatus ? (
                    <div className="text-muted-foreground/70 text-[11px]">
                        {formatStatusLabel(caregiver.groupExpansionStatus)}
                    </div>
                ) : null}
            </td>
            <td className="space-y-0.5 py-2 pr-3">
                <div className="text-foreground">
                    {caregiver.selectedOrSubmitted
                        ? 'Submitted'
                        : 'Not submitted'}
                </div>
                <div className="text-muted-foreground/70 text-[11px]">
                    {caregiver.submittedShiftIds.length > 0
                        ? `${caregiver.submittedShiftIds.length} shifts`
                        : 'No shifts'}
                </div>
            </td>
            <td className="space-y-0.5 py-2 pr-3">
                <div className="text-foreground tabular-nums">
                    {formatCountMetric(caregiver.contactAttemptCount)} attempts
                </div>
                <div className="text-muted-foreground/70 text-[11px] tabular-nums">
                    {formatCountMetric(caregiver.shiftResponseCount)} responses
                </div>
            </td>
            <td className="space-y-0.5 py-2">
                <div className="text-foreground">
                    {formatStatusLabel(caregiver.evidenceStatus)}
                </div>
                {caregiver.droppedOrSkippedReason ? (
                    <div className="text-muted-foreground/70 text-[11px]">
                        {formatStatusLabel(caregiver.droppedOrSkippedReason)}
                    </div>
                ) : null}
            </td>
        </tr>
    );
}

function CoverageLine({
    label,
    values,
}: {
    readonly label: string;
    readonly values: ReadonlyArray<string>;
}) {
    return (
        <div className="text-[11px]">
            <span className="text-muted-foreground/70">{label}</span>{' '}
            <span className="text-foreground font-mono break-all">
                {values.length > 0 ? values.join(', ') : 'none'}
            </span>
        </div>
    );
}

function getOutreachReconciliationPayload(
    payload: AdminAgentArtifact['payload'],
): OutreachReconciliationPayload {
    const payloadRecord = toRecord(payload);
    const caregivers = Array.isArray(payloadRecord['caregivers'])
        ? payloadRecord['caregivers']
              .filter(isRecord)
              .flatMap(outreachReconciliationCaregiverFromRecord)
        : [];
    return {
        summary: toRecord(payloadRecord['summary']),
        mismatches: toRecord(payloadRecord['mismatches']),
        caregivers,
    };
}

function outreachReconciliationCaregiverFromRecord(
    record: Record<string, unknown>,
): ReadonlyArray<OutreachReconciliationCaregiver> {
    const caregiverId = stringValue(record['caregiver_id']);
    if (!caregiverId) {
        return [];
    }
    const suggestionSource = toRecord(record['suggestion_source']);
    return [
        {
            caregiverId,
            caregiverName: stringValue(record['caregiver_name']),
            savedSuggestion: record['saved_suggestion'] === true,
            suggestionSourceKind: stringValue(suggestionSource['kind']),
            suggestionSourceMode: stringValue(suggestionSource['mode']),
            rawCoveredShiftIds: arrayStrings(record['raw_covered_shift_ids']),
            coveredShiftGroupIds: arrayStrings(
                record['covered_shift_group_ids'],
            ),
            expandedCoveredShiftIds: arrayStrings(
                record['expanded_covered_shift_ids'],
            ),
            groupExpansionStatus: stringValue(record['group_expansion_status']),
            selectedOrSubmitted: record['selected_or_submitted'] === true,
            submittedShiftIds: arrayStrings(record['submitted_shift_ids']),
            contactAttemptCount: numberValue(record['contact_attempt_count']),
            shiftResponseCount: numberValue(
                record['contact_attempt_shift_response_count'],
            ),
            droppedOrSkippedReason: stringValue(
                record['dropped_or_skipped_reason'],
            ),
            evidenceStatus: stringValue(record['evidence_status']),
        },
    ];
}

function formatSuggestionSource(
    caregiver: OutreachReconciliationCaregiver,
): string {
    const source = formatStatusLabel(caregiver.suggestionSourceKind);
    if (caregiver.suggestionSourceMode) {
        return `${source} · ${caregiver.suggestionSourceMode}`;
    }
    return source;
}
